// Service 13 — Thai driving licence & International Driving Permit (IDP).
// Category definitions only (client-bundle safe, no corpus import).

export type DrivingFaqCategory =
  | "thai-licence-first-time"
  | "thai-licence-renewal"
  | "foreigner-thai-licence"
  | "foreign-licence-conversion"
  | "international-driving-permit"
  | "driving-abroad"
  | "licence-translation-certification"
  | "medical-training-tests"
  | "lost-damaged-replacement"
  | "rental-insurance-penalties";

export interface DrivingCategoryDef {
  slug: DrivingFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const DRIVING_FAQ_CATEGORIES: DrivingCategoryDef[] = [
  {
    slug: "thai-licence-first-time",
    titleTh: "ขอใบอนุญาตขับรถไทยครั้งแรก",
    titleEn: "Applying for a First Thai Driving Licence",
    descTh:
      "ใบอนุญาตขับรถส่วนบุคคลชั่วคราว (2 ปี) เอกสารที่ต้องใช้ การอบรม การทดสอบข้อเขียนและทดสอบขับรถ ที่สำนักงานขนส่งกรมการขนส่งทางบก",
    descEn:
      "The two-year temporary private driving licence: documents, mandatory training, theory test and practical driving test at Department of Land Transport offices.",
    matchTh: /ครั้งแรก|ชั่วคราว|สอบข้อเขียน|ทดสอบขับรถ|อายุ 18|จองคิว/,
    matchEn: /first|temporary|theory test|practical|booking|age 18/i,
  },
  {
    slug: "thai-licence-renewal",
    titleTh: "ต่ออายุและเปลี่ยนชนิดใบอนุญาตขับรถ",
    titleEn: "Renewing and Upgrading a Thai Driving Licence",
    descTh:
      "ต่ออายุจากชั่วคราว 2 ปี เป็น 5 ปี ต่ออายุ 5 ปีเป็น 5 ปี การอบรมออนไลน์ (e-Learning) และการต่ออายุล่วงหน้าหรือขาดต่อ",
    descEn:
      "Upgrading the two-year licence to five years, five-year renewals, DLT e-Learning training and rules for early or overdue renewal.",
    matchTh: /ต่ออายุ|5 ปี|e-Learning|อบรมออนไลน์|ขาดต่อ|หมดอายุ/,
    matchEn: /renew|five-year|e-Learning|expired|overdue/i,
  },
  {
    slug: "foreigner-thai-licence",
    titleTh: "ชาวต่างชาติขอใบขับขี่ไทย",
    titleEn: "Foreigners Applying for a Thai Licence",
    descTh:
      "หลักฐานที่อยู่ในไทย (ใบรับรองถิ่นที่อยู่จากตรวจคนเข้าเมือง สถานทูต หรือใบอนุญาตทำงาน) หนังสือเดินทางและวีซ่า และขั้นตอนที่สำนักงานขนส่ง",
    descEn:
      "Proof of residence in Thailand (immigration or embassy residence certificate, or a work permit), passport and visa requirements, and the DLT process.",
    matchTh: /ต่างชาติ|ใบรับรองถิ่นที่อยู่|หนังสือเดินทาง|วีซ่า|ใบอนุญาตทำงาน/,
    matchEn: /foreigner|residence certificate|passport|visa|work permit/i,
  },
  {
    slug: "foreign-licence-conversion",
    titleTh: "ใช้และเทียบใบขับขี่ต่างประเทศในไทย",
    titleEn: "Using and Converting a Foreign Licence in Thailand",
    descTh:
      "ขับรถในไทยด้วยใบขับขี่ต่างประเทศหรือใบขับขี่สากลได้แค่ไหน การยกเว้นการทดสอบเมื่อยื่นใบขับขี่ต่างประเทศที่ยังไม่หมดอายุ และเอกสารรับรองที่ต้องใช้",
    descEn:
      "How long you may drive on a foreign or international permit, test exemptions when submitting a valid foreign licence, and the certification documents required.",
    matchTh: /ใบขับขี่ต่างประเทศ|เทียบ|ยกเว้นการทดสอบ|แลกใบขับขี่/,
    matchEn: /foreign licence|foreign license|convert|exempt|exchange/i,
  },
  {
    slug: "international-driving-permit",
    titleTh: "ใบอนุญาตขับรถระหว่างประเทศ (ใบขับขี่สากล)",
    titleEn: "International Driving Permit Issued in Thailand",
    descTh:
      "ใบขับขี่สากล 2 แบบตามอนุสัญญาเจนีวา 1949 และอนุสัญญาเวียนนา 1968 อายุการใช้งาน เอกสารที่ต้องใช้ และสำนักงานที่ให้บริการ",
    descEn:
      "The two Thai IDP types under the 1949 Geneva and 1968 Vienna Conventions: validity, required documents and where to apply.",
    matchTh: /ใบขับขี่สากล|ระหว่างประเทศ|เจนีวา|เวียนนา|IDP/,
    matchEn: /international driving permit|IDP|Geneva|Vienna/i,
  },
  {
    slug: "driving-abroad",
    titleTh: "ขับรถในต่างประเทศด้วยใบขับขี่ไทย",
    titleEn: "Driving Abroad on a Thai Licence",
    descTh:
      "ต้องพกใบขับขี่ไทยคู่กับใบขับขี่สากลหรือไม่ ประเทศที่ใช้อนุสัญญาต่างกัน และเอกสารที่บริษัทเช่ารถและตำรวจจราจรมักเรียกดู",
    descEn:
      "Whether you must carry the Thai licence with the IDP, which convention each country recognises, and what rental firms and traffic police ask for.",
    matchTh: /ต่างประเทศ|เช่ารถต่างประเทศ|ยุโรป|ญี่ปุ่น|ออสเตรเลีย|อเมริกา/,
    matchEn: /abroad|overseas|Europe|Japan|Australia|United States/i,
  },
  {
    slug: "licence-translation-certification",
    titleTh: "แปลและรับรองใบขับขี่",
    titleEn: "Translating and Certifying a Driving Licence",
    descTh:
      "แปลใบขับขี่ไทยเป็นภาษาอังกฤษหรือภาษาอื่น รับรองคำแปลที่กรมการกงสุล การรับรองโดย Notarial Services Attorney และการรับรองสถานทูต",
    descEn:
      "Translating a Thai or foreign licence, Thai MFA translation certification, Notarial Services Attorney certification and embassy attestation.",
    matchTh: /แปล|คำแปล|รับรองคำแปล|กงสุล|โนตารี|Notar/,
    matchEn: /translat|certif|MFA|notar|embassy/i,
  },
  {
    slug: "medical-training-tests",
    titleTh: "ใบรับรองแพทย์ การอบรม และการทดสอบ",
    titleEn: "Medical Certificate, Training and Tests",
    descTh:
      "ใบรับรองแพทย์ตามแบบที่แพทยสภากำหนด อายุใบรับรอง การทดสอบสมรรถภาพร่างกาย และหัวข้อการอบรมของกรมการขนส่งทางบก",
    descEn:
      "The Medical Council certificate format and validity, physical aptitude tests and the DLT training curriculum.",
    matchTh: /ใบรับรองแพทย์|แพทยสภา|สมรรถภาพ|ตาบอดสี|อบรม/,
    matchEn: /medical certificate|Medical Council|aptitude|colour|training/i,
  },
  {
    slug: "lost-damaged-replacement",
    titleTh: "ใบขับขี่หาย ชำรุด และแก้ไขข้อมูล",
    titleEn: "Lost, Damaged or Amended Licences",
    descTh:
      "ขอใบแทนกรณีใบขับขี่สูญหายหรือชำรุด การแก้ไขชื่อ-นามสกุลหรือที่อยู่ และใบขับขี่อิเล็กทรอนิกส์ในแอป DLT QR Licence",
    descEn:
      "Replacement for lost or damaged licences, changing name or address details, and the electronic licence in the DLT QR Licence app.",
    matchTh: /หาย|สูญหาย|ชำรุด|ใบแทน|เปลี่ยนชื่อ|DLT QR/,
    matchEn: /lost|damaged|replacement|change of name|DLT QR/i,
  },
  {
    slug: "rental-insurance-penalties",
    titleTh: "เช่ารถ ประกันภัย และบทลงโทษ",
    titleEn: "Car Rental, Insurance and Penalties",
    descTh:
      "เอกสารที่บริษัทเช่ารถในไทยเรียกดู ผลของการขับรถโดยไม่มีใบอนุญาตต่อการเคลมประกัน และโทษตามพระราชบัญญัติรถยนต์และกฎหมายจราจรทางบก",
    descEn:
      "What Thai rental companies require, how driving unlicensed affects insurance claims, and penalties under the Motor Vehicle Act and traffic law.",
    matchTh: /เช่ารถ|ประกัน|เคลม|โทษ|ปรับ|ไม่มีใบอนุญาต/,
    matchEn: /rental|insurance|claim|penalt|fine|unlicensed/i,
  },
];

export function getDrivingCategoryDef(slug: string): DrivingCategoryDef | undefined {
  return DRIVING_FAQ_CATEGORIES.find((c) => c.slug === slug);
}